import {
  ApplicationConfig,
  DOCUMENT,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import {
  provideClientHydration,
  withHttpTransferCacheOptions,
  withI18nSupport,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { WindowProvider, windowProvider } from './core/window.provider';

import { FirebaseApp } from '@angular/fire/app';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { AuthGuard } from '@angular/fire/auth-guard';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { LogLevel, setLogLevel } from '@angular/fire';
import { DateFnsAdapter, MAT_DATE_FNS_FORMATS, provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { enUS } from 'date-fns/locale';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { NG_SCROLLBAR_OPTIONS } from 'ngx-scrollbar';
import { IconRegistryService } from '@gains-com/ui-angular';
import { environment } from '../environments/environment';
import { authInterceptorFn } from './auth/auth.interceptor';
import { httpTransferCacheFilter } from './core/http-transfer-cache-filter';
import { httpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { sentryTraceInterceptor } from './core/interceptors/sentry-trace.interceptor';
import { Platform } from './core/platform.service';
import { routes } from './app.routes';
import { SatPopoverModule } from '@ncstate/sat-popover';
import { LOGGER_FACTORY, loggerFactoryProvider } from './core/logger/logger-factory.provider';
import { REALTIME, realtimeProvider } from './core/realtime/realtime.provider';
import { ILoggerFactory } from './core/logger/logger-interface';
import { DatePipe } from '@angular/common';

if (isDevMode()) {
  setLogLevel(LogLevel.WARN);
} else {
  setLogLevel(LogLevel.SILENT);
}

/**
 * Base configuration for both server side rendered app and client side rendered.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimationsAsync(), // If we ever need animations immediately, we should use the eager version
    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'top',
      }),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
    ),
    provideClientHydration(
      withIncrementalHydration(),
      // withEventReplay(),
      withI18nSupport(),
      withHttpTransferCacheOptions({
        includeRequestsWithAuthHeaders: true,
        filter: httpTransferCacheFilter,
      }),
    ),
    provideHttpClient(withFetch(), withInterceptors([sentryTraceInterceptor, httpErrorInterceptor, authInterceptorFn])),
    provideDateFnsAdapter(),
    { provide: LOCALE_ID, useValue: 'en-US' }, // TODO: This needs to change once we support multiple locales
    { provide: MAT_DATE_LOCALE, useValue: enUS },
    { provide: DateAdapter, useClass: DateFnsAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },
    importProvidersFrom(SatPopoverModule),
    {
      provide: WindowProvider,
      useFactory: (document: Document) => windowProvider(document),
      deps: [DOCUMENT],
    },
    {
      provide: REALTIME,
      useFactory: (_platform: Platform, _loggerFactory: ILoggerFactory) =>
        realtimeProvider(_platform, _loggerFactory.create('REALTIME')),
      deps: [Platform, LOGGER_FACTORY],
    },
    {
      provide: LOGGER_FACTORY,
      useFactory: (_platform: Platform) => loggerFactoryProvider(_platform),
      deps: [Platform],
    },
    provideAuth(() => {
      const auth = getAuth(inject(FirebaseApp));
      if ((auth as any)._canInitEmulator && environment.emulatorPorts?.auth) {
        connectAuthEmulator(auth, `http://localhost:${environment.emulatorPorts.auth}`, { disableWarnings: true });
      }
      return auth;
    }),
    DatePipe,
    AuthGuard,
    // Register icons (safe for both server and client)
    // Browser-only attribution tracking moved to app.config.client.ts
    provideAppInitializer(() => {
      const iconRegistryService = inject(IconRegistryService);
      iconRegistryService.registerIcons(environment.env === 'production');
    }),
    {
      provide: NG_SCROLLBAR_OPTIONS,
      useValue: {
        visibility: 'hover',
      },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
  ],
};
